import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flame, Send } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { PostedList } from '@/components/PostedList';
import { refreshPostedSoon } from '@/lib/posted';

/**
 * NSFW quick-panel — one button per category. Discord requires the target
 * channel to be NSFW-flagged; the bot's dispatcher validates and errors
 * loudly if it isn't.
 */

const TYPES = ['4k', 'ass', 'boobs', 'hentai', 'pussy'] as const;

export function NsfwPanel() {
  const { guildId } = useParams<{ guildId: string }>();
  const [channelId, setChannelId] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const postMut = useMutation({
    mutationFn: (nsfwType: string) =>
      api('/api/actions/nsfw-post', {
        method: 'POST',
        body: JSON.stringify({ guildId, channelId, nsfwType }),
      }),
    onSuccess: () => {
      setPending(null);
      setError(null);
      refreshPostedSoon(qc, guildId!);
    },
    onError: (err: Error) => {
      setPending(null);
      setError(err.message);
    },
  });

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="mb-2 flex items-center gap-2 text-2xl font-semibold">
        <Flame className="h-6 w-6 text-primary" /> NSFW-Panel
      </h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Postet ein Random-Bild aus der gewählten Kategorie in den Ziel-Channel.
        Der Channel muss in Discord als <strong>NSFW/altersbeschränkt</strong>{' '}
        markiert sein, sonst bricht Discord den Post ab.
      </p>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Kategorie posten</CardTitle>
          <CardDescription>
            Ziel-Channel wählen und auf eine Kategorie klicken.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>NSFW-Channel</Label>
            <ChannelPicker
              guildId={guildId!}
              value={channelId}
              onChange={setChannelId}
              types={[CHANNEL_TYPE.GUILD_TEXT]}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {TYPES.map((t) => (
              <Button
                key={t}
                variant="outline"
                disabled={!channelId || pending === t}
                onClick={() => {
                  setPending(t);
                  postMut.mutate(t);
                }}
              >
                <Send className="mr-1 h-4 w-4" />
                {pending === t ? 'Poste…' : t}
              </Button>
            ))}
          </div>

          {postMut.isSuccess && !error && (
            <p className="text-sm text-success">Gepostet ✓</p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="border-t pt-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Bereits gepostet
            </div>
            <PostedList guildId={guildId!} type="nsfw" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
